import React, { useMemo, useState, useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import { compose } from "redux";
import { withStyles, withBreakpoint, withMobile } from "components/common";
import Table from "components/common/Table";
import TableFilter from "components/common/TableFilter";
import InputContainer from "components/common/ui/InputContainer";
import Dropdown from "components/common/Dropdown";
import snakeCase from "lodash/snakeCase";
import startCase from "lodash/startCase";
import { APPLICATION_STATUS } from "utils/constants";

const moment = require("moment");

const mapping = {};
const tableFilter = (
  <div className="flex">
    <TableFilter
      mapping={mapping}
      containerClassName="flex-initial text-gray-primary font-light"
      initialActive="All"
    />
  </div>
);

const tags = props => {
  // const { value: { tags, conspirators }, column: { getProps: { displayConspirators } } } = props;
  const {
    value: { tags, conspirators, setFilterByTag }
  } = props;

  const tagsToDisplay = tags.map(t => {
    if (['vip','vip2'].includes(t)) {
      return (
      <span 
        onClick={() => setFilterByTag(t)}
        className="rounded-md border mx-1 px-1 py-1 border-green-400 text-green-500 text-xs">
          {t}
      </span>)
    } else {
      return <span
        onClick={() => setFilterByTag(t)}
        className="cursor-pointer rounded-md border mx-1 px-1 py-1 border-red-400 text-red-500 text-xs"
      >{t}</span>
    }
  });
  return (
    <div>
      <div className="flex-1 flex-wrap ml-1 md:ml-0">
        {tagsToDisplay}
      </div>
      <div>
        {conspirators.map(c => {
          const limit = c.credit && c.credit.credit_limit ? ` for $${c.credit.credit_limit}` : "";
          const str = `${c.email} (${c.first_name} ${c.last_name}) - ${c.application_status} ${limit}`;
          const colors = {
            approved: "text-green-600",
            preapproved: "text-gray-800",
            pending: "text-blue-600",
            rejected: "text-red-600"
          }
          const styl = c.application_status ? colors[c.application_status] : "";
          const link =
            c.application_status === "approved" ? (
              <Link
                className={styl}
                to={{
                  pathname: `/admin/god/user?email=${encodeURIComponent(
                    c.email
                  )}`
                }}
                target="_blank"
              >
                {str}
              </Link>
            ) : (
              <Link
                className={styl}
                to={{
                  pathname: `/supportal/user?email=${encodeURIComponent(
                    c.email
                  )}`
                }}
                target="_blank"
              >
                {str}
              </Link>
            );
          return <div className="text-xs mx-2">{link}</div>
        })}
      </div>
    </div>
  )
}


const computeColumns = (belowMd, handleTooltipOpen, idMapping, { setFilterByTag }) =>
  belowMd
    ? [
        {
          Header: "Name",
          accessor: data => `${data.first_name} ${data.last_name}`
        },
        {
          Header: "Status",
          accessor: "application_status"
        }
      ]
    : [
        {
          Header: "Name",
          accessor: data => {
            return data.application_status === "approved" ? (
              <Link
                to={{
                  pathname: `/admin/god/user?email=${encodeURIComponent(
                    data.email
                  )}`
                }}
                target="_blank"
              >
                {data.first_name} {data.last_name}
              </Link>
            ) : (
              <Link
                to={{
                  pathname: `/supportal/user?email=${encodeURIComponent(
                    data.email
                  )}`
                }}
                target="_blank"
              >
                {data.first_name} {data.last_name}
              </Link>
            );
          }
        },
        {
          Header: "Email",
          accessor: data => `${data.email}`
        },
        {
          Header: "Tags",
          accessor: data => {
            const { tags, metadata } = data;
            const displayTags = new Set([...tags || []]);
            const { fraud_reason, bank_shared_with, address_shared_with, phone_shared_with, ip_shared_with } = JSON.parse(metadata) || {};
            (fraud_reason || []).forEach(fr => {
              displayTags.add(fr);
            });
            const ppl = new Set([...(bank_shared_with || []), ...(address_shared_with || []), ...(phone_shared_with || []), ...(ip_shared_with || [])]);
            // console.dir(ppl)
            return {
              tags: [...displayTags],
              conspirators: ([...ppl || []].map(p => (idMapping[p]) || [])).filter(p => p.id !== data.id),
              setFilterByTag
            }
          },
          Cell: tags
          // getProps: { displayConspirators: () => handleTooltipOpen()}
        },
        {
          Header: "Limit",
          accessor: data => `$${data.credit ? data.credit.credit_limit : 0}`
        },
        {
          Header: "App Status",
          accessor: data => data.application_status || "pending"
        },
        {
          Header: "Sign up date",
          accessor: "created_at",
          Cell: ({ value }) =>
            value ? moment.unix(value / 1000).format("M/D/YYYY") : "N/A"
        },
        {
          Header: "Approval date",
          accessor: "approved_at",
          Cell: ({ value }) =>
            value ? moment.unix(value / 1000).format("M/D/YYYY") : "N/A"
        },
        {
          Header: "Total Spend",
          accessor: "total_spend",
          Cell: ({ value }) => `$${value || 0}`
        }
      ];

const FraudTable = ({
  fraudsters = [],
  loading,
  breakpointContext: { isBelow }
}) => {

  const [filteredFraudsters, setFilteredFraudsters] = useState(fraudsters);
  const [openTooltip, setOpenTooltip] = useState(false);
  const [filterByTag, setFilterByTag] = useState("All");
  const history = useHistory();

  const idMapping = {};
  fraudsters.forEach(f => {
    idMapping[f.id] = f;
  })

  const handleTooltipClose = () => {
    setOpenTooltip(false);
  };
  const handleTooltipOpen = () => {
    setOpenTooltip(true);
  };
  const searchByTag = searchStr => {
    if (searchStr === 'All'){
      setFilteredFraudsters(fraudsters);
    } else {
      setFilteredFraudsters(fraudsters.filter(f => {
        const searchFields = f.tags || [];
        if (f.metadata && typeof f.metadata === 'string') {
          searchFields.push(...(JSON.parse(f.metadata) || {}).fraud_reason || [])
        };
        return searchFields.includes(searchStr)
      }))
    }
  };

  useEffect(() => {
    searchByTag(filterByTag)
  }, [filterByTag])

  const columns = useMemo(() => computeColumns(isBelow("md"), handleTooltipOpen, idMapping, { setFilterByTag }), [isBelow("md"), handleTooltipOpen, idMapping]);


  const approvedFraudsters = fraudsters.filter(
    ref => ref.application_status === APPLICATION_STATUS.APPROVED
  );
  const pendingFraudsters = fraudsters.filter(
    ref => ref.application_status === APPLICATION_STATUS.PENDING
  );
  const rejectedFraudsters = fraudsters.filter(
    ref => ref.application_status === APPLICATION_STATUS.REJECTED
  );

  mapping.all = {
    name: `All (${fraudsters.length})`,
    fn: () => setFilteredFraudsters(fraudsters)
  };
  mapping.approved = {
    name: `Approved (${approvedFraudsters.length})`,
    fn: () => setFilteredFraudsters(approvedFraudsters)
  };
  mapping.pending = {
    name: `Pending (${pendingFraudsters.length})`,
    fn: () => setFilteredFraudsters(pendingFraudsters)
  };
  mapping.rejected = {
    name: `Rejected (${rejectedFraudsters.length})`,
    fn: () => setFilteredFraudsters(rejectedFraudsters)
  };

  const allTags = new Set(['All']);
  for (const f of fraudsters){
    const arr = f.tags || [];
    if (f.metadata && typeof f.metadata === 'string') {
      arr.push(...(JSON.parse(f.metadata) || {}).fraud_reason || [])
    };
    arr.forEach(allTags.add, allTags)
  };
  const allTagsConfig = Array.from(allTags);

  const getRowStyle = row => {
    return {
      backgroundColor:
        row.original.application_status === "rejected"
          ? "#FFE4E5"
          : (row.original.application_status === "approved"
            ? "#E5FFE5" : "#FFE9AB")
    };
  };

  return (
    <div className="mt-16">
      <h1 className="text-2xl font-title mb-2 font-light">
        Admin: List of Potential Fraudsters
      </h1>
      
      <div className="my-6">
        {loading ? (
          <div>Loading...</div>
        ) : fraudsters.length ? (
          <div>
            <div className='flex items-center'>
              {tableFilter}
              <span className='w-56 mx-6'>
                  <Dropdown
                    selectedElement={{
                      name: startCase(filterByTag),
                      value: filterByTag
                    }}
                    componentProps={{
                      className: "px-3 py-2"
                    }}
                    elements={allTagsConfig
                      .sort((a, b) => {
                        if (a === 'All') return -1;
                        if (a < b) return -1
                        return 1;
                      })
                    }
                    didSelect={(tag) => {
                      setFilterByTag(tag)
                      console.log(tag)
                    }}
                    leftAligned
                    border
                  />
              </span>
            </div>
            <div className='mt-6'>
                <Table
                  id="fraud-table"
                  data={filteredFraudsters}
                  columns={columns}
                  getRowStyle={getRowStyle}
                  itemsPerPage={20}
                />
            </div>
          </div>
        ) : (
          <div>Currently there are no fraudsters.</div>
        )}
      </div>
    </div>
  );
};

export default compose(withMobile, withStyles, withBreakpoint)(FraudTable);
